import React from 'react';

export type StatusTone = 'positive' | 'caution' | 'critical' | 'info' | 'neutral';

interface StatusLabelProps {
  tone?: StatusTone;
  children: React.ReactNode;
  /** Brighter dot/text palette legible on navy surfaces (banners). */
  light?: boolean;
  className?: string;
  title?: string;
}

const DARK_DOT: Record<StatusTone, string> = {
  positive: 'var(--positive-700)',
  caution: 'var(--caution-700)',
  critical: 'var(--claret-700)',
  info: 'var(--navy-700)',
  neutral: 'var(--ink-400)',
};

const LIGHT_DOT: Record<StatusTone, string> = {
  positive: '#9BC9A4',
  caution: '#E8C089',
  critical: '#E8A4A8',
  info: '#A8C0E0',
  neutral: 'rgba(255,255,255,0.75)',
};

/**
 * StatusLabel — the in-app status workhorse: a small colored dot + a roman
 * sans label. Quiet, unambiguous, never mistakable for a link or a button.
 * Replaces filled pill badges for status display throughout the app.
 */
const StatusLabel: React.FC<StatusLabelProps> = ({
  tone = 'neutral',
  children,
  light = false,
  className = '',
  title,
}) => (
  <span
    className={`inline-flex items-center gap-2 font-sans whitespace-nowrap ${className}`}
    style={{
      fontSize: 13,
      color: light ? 'rgba(255,255,255,0.92)' : 'var(--ink-900)',
    }}
    title={title}
  >
    <span
      className="inline-block rounded-full flex-shrink-0"
      style={{ width: 6, height: 6, background: (light ? LIGHT_DOT : DARK_DOT)[tone] }}
    />
    <span>{children}</span>
  </span>
);

export default StatusLabel;
